import getSymbolFromCurrency from 'currency-symbol-map';

import { env } from '@/constants/env';

/**
 * Locale (en-US, fr-CA, it-IT, etc)
 */
export const locale = env.VITE_I18N_LOCALE || 'en-US';
const defaultCurrency = import.meta.VITE_I18N_DEFAULT_CURRENCY || 'USD';

/**
 * @property {string} default - Currency symbol ($, €, etc)
 * @property {string} symbol - ISO 4217 currency code (USD, CAD, EUR, etc)
 */
export const currency = {
  /**
   * DEFAULT_CURRENCY is based on the currency of the current company.
   * Always try use the currency code returned with an amount when provided by
   * the backend instead.
   */
  default: defaultCurrency,
  position: getLocaleSymbolPosition(),
  symbol: getCurrencySymbol(defaultCurrency),
};

/**
 * Locale-specific decimal separator ('.', ',')
 */
export const decimalSeparator = getLocaleDecimalSeparator(locale);

/**
 * A helper function for obtaining the current currency symbol
 * @param {Object} [instance] - Optional VueI18n instance
 * @returns {string} Currency symbol ('$', '€', etc)
 */
function getCurrencySymbol(code) {
  return getSymbolFromCurrency(code);
}

/**
 * A helper function for obtaining the current decimal separator
 * @param {string} [locale] - Optional locale
 * @returns {string} Decimal separator ('.', ',')
 */
function getLocaleDecimalSeparator(_locale = locale) {
  // Fallback
  if (!Intl.NumberFormat.prototype.formatToParts) {
    return (1.1).toLocaleString(_locale).replace(/\d/g, '');
  }

  return new Intl.NumberFormat(_locale, {
    style: 'decimal',
  })
    .formatToParts(1.1)
    .find((part) => part.type === 'decimal').value;
}

/**
 * A helper function for obtaining symbol position relative to a value
 * This should be fine going forward unless we get into RTL, or Arabic.
 * @param {string} type the type of symbol
 */
export function getLocaleSymbolPosition({ type = 'currency' } = {}) {
  return (type === 'percent' && percent(1).indexOf('%') === 0) ||
    (type === 'currency' && money({ amount: 1 }).indexOf(getCurrencySymbol(defaultCurrency)) === 0)
    ? 'prepend'
    : 'append';
}

/**
 * Format a value as a decimal in a locale-aware format.
 * NOTE: Be careful as this returns a string and should not be used for
 * additional calculation unless converted.
 * @param {number|string} value number to format
 * @param {number} [decimalPlaces] number of decimal places
 * @return {string} locale-aware number as a string
 */
export function decimal(value, decimalPlaces) {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    style: 'decimal',
  }).format(value);
}

/**
 * @typedef {Object} Money
 * @property {number} amount - number to format
 * @property {currency} [symbol] - ISO 4217 currency code (USD, CAD, EUR, etc)
 */

/**
 * Format a value as currency in a locale-aware format.
 * @param {Money} params - object with amount and currency type
 * @param params.amount - amount number
 * @param params.currency - ISO 4217 currency code
 * @param params.options - additional Intl.NumberFormat `options`
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#parameters
 * @returns {string} locale-aware currency as a string
 */
export function money(data) {
  // Money can receive info as number or Money typed object.
  // In case of receiving value as number, it assumes the received value as the amount
  // This is happening across the app, so we need to document this usage in our code
  // If data or data.amount value is null or undefined, it will be set as number zero

  let amount = data?.amount ?? data;

  if (isNaN(amount)) {
    amount = 0;
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: data?.currency || defaultCurrency,
    ...(data?.options || {}),
  }).format(amount);
}

/**
 * Formats an integer Money amount. Calculations with integers are not prone to floating point errors unlike calculations with decimal numbers.
 * The amount is assumed to be in cents (or the smallest unit of the currency).
 * For example, an amount of 100 will be formatted as $1.00.
 * @param {number} amount - the Money amount. Must be an integer.
 * @param {Object} options - options for formatting.
 * @param {string} options.currency - the currency code (default is USD).
 * @returns {string} - the formatted currency string.
 */
export function moneyInt(amount, { currency = defaultCurrency } = {}) {
  const majorUnits = amount / 100;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(majorUnits);
  } catch (error) {
    return majorUnits.toFixed(2);
  }
}

/**
 * Format a value as a percent in a locale-aware format.
 * @param {number|string} value number to format
 * @param {number} [decimalPlaces] number of decimal places
 * @return {string} locale-aware percentage value as a string
 */
export function percent(value, decimalPlaces) {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces ?? 2,
  }).format(value);
}

/**
 * Cleans a value to reflect type=number restrictions.
 * Omits exponents, and restricts the decimal separator.
 * @param {number|string} value value to format
 * @param {string} value decimal separator
 */
export function sanitizeDecimal(value, separator = '.') {
  const replace = `[^0-9${separator}+-]*`;
  const re = new RegExp(replace, 'g');

  return `${value}`.replace(re, '');
}

/**
 * Replace decimal separator of `value` with `convertTo`
 * @param {string} value
 * @param {string} convertTo decimal character to update the value with
 */
export function convertDecimal(value, convertTo = '.') {
  return `${value}`.replace(convertTo === '.' ? ',' : '.', convertTo);
}

/**
 * Normalize locale format
 * Always assumes {lang}-{region} format
 * @param {string} locale the locale to format
 */
export function normalizeLocale(_locale = locale) {
  const [lang, region] = _locale.replace('_', '-').split('-');

  return `${lang}-${region.toUpperCase()}`;
}
